import {Component, DoCheck, HostBinding, Input, OnDestroy, Optional, Self, ChangeDetectionStrategy} from '@angular/core';
import {ControlValueAccessor, FormControl, NgControl} from '@angular/forms';
import {MatFormFieldControl} from '@angular/material/form-field';
import {Subject} from 'rxjs';

@Component({
    selector: 'app-state',
    templateUrl: './state.component.html',
    styleUrls: ['./state.component.scss'],
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: StateComponent
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StateComponent implements ControlValueAccessor, MatFormFieldControl<string>, DoCheck, OnDestroy {

  static nextId = 0;

  @HostBinding() id = `zip-code-input-${StateComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedBy = '';

  protected states = [
    { name: 'Alabama', abbreviation: 'AL' },
    { name: 'Alaska', abbreviation: 'AK' },
    { name: 'Arizona', abbreviation: 'AZ' },
    { name: 'Arkansas', abbreviation: 'AR' },
    { name: 'California', abbreviation: 'CA' },
    { name: 'Colorado', abbreviation: 'CO' },
    { name: 'Connecticut', abbreviation: 'CT' },
    { name: 'Delaware', abbreviation: 'DE' },
    { name: 'Florida', abbreviation: 'FL' },
    { name: 'Georgia', abbreviation: 'GA' },
    { name: 'Hawaii', abbreviation: 'HI' },
    { name: 'Idaho', abbreviation: 'ID' },
    { name: 'Illinois', abbreviation: 'IL' },
    { name: 'Indiana', abbreviation: 'IN' },
    { name: 'Iowa', abbreviation: 'IA' },
    { name: 'Kansas', abbreviation: 'KS' },
    { name: 'Kentucky', abbreviation: 'KY' },
    { name: 'Louisiana', abbreviation: 'LA' },
    { name: 'Maine', abbreviation: 'ME' },
    { name: 'Maryland', abbreviation: 'MD' },
    { name: 'Massachusetts', abbreviation: 'MA' },
    { name: 'Michigan', abbreviation: 'MI' },
    { name: 'Minnesota', abbreviation: 'MN' },
    { name: 'Mississippi', abbreviation: 'MS' },
    { name: 'Missouri', abbreviation: 'MO' },
    { name: 'Montana', abbreviation: 'MT' },
    { name: 'Nebraska', abbreviation: 'NE' },
    { name: 'Nevada', abbreviation: 'NV' },
    { name: 'New Hampshire', abbreviation: 'NH' },
    { name: 'New Jersey', abbreviation: 'NJ' },
    { name: 'New Mexico', abbreviation: 'NM' },
    { name: 'New York', abbreviation: 'NY' },
    { name: 'North Carolina', abbreviation: 'NC' },
    { name: 'North Dakota', abbreviation: 'ND' },
    { name: 'Ohio', abbreviation: 'OH' },
    { name: 'Oklahoma', abbreviation: 'OK' },
    { name: 'Oregon', abbreviation: 'OR' },
    { name: 'Pennsylvania', abbreviation: 'PA' },
    { name: 'Rhode Island', abbreviation: 'RI' },
    { name: 'South Carolina', abbreviation: 'SC' },
    { name: 'South Dakota', abbreviation: 'SD' },
    { name: 'Tennessee', abbreviation: 'TN' },
    { name: 'Texas', abbreviation: 'TX' },
    { name: 'Utah', abbreviation: 'UT' },
    { name: 'Vermont', abbreviation: 'VT' },
    { name: 'Virginia', abbreviation: 'VA' },
    { name: 'Washington', abbreviation: 'WA' },
    { name: 'West Virginia', abbreviation: 'WV' },
    { name: 'Wisconsin', abbreviation: 'WI' },
    { name: 'Wyoming', abbreviation: 'WY' }
  ];

  @Input() placeholder = '';
  @Input() required = false;
  @Input() disabled = false;

  value: string | null = null;

  stateChanges = new Subject<void>();
  focused = false;
  errorState = false;
  controlType = 'zip-code-input';

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  // Called when the value changes
  writeValue(value: any): void {
    this.value = value;
    this.stateChanges.next();
  }

  // Registers a callback function for when the control's value changes in the UI
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // Registers a callback function for when the control is touched
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // Called when the component is disabled or enabled
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.stateChanges.next();
  }

  // Update value and notify parent form
  handleChange(value: string): void {
    this.value = value;
    this.onChange(value); // Notify Angular form control
    this.onTouched(); // Mark as touched
    this.stateChanges.next();
  }

  ngDoCheck(): void {
    const control = this.ngControl?.control;
    if (!control) {
      return;
    }

    const nextErrorState = !!(control.invalid && (control.touched || control.dirty));
    const nextRequired = this.hasRequiredValidator(control.validator);

    if (this.errorState !== nextErrorState) {
      this.errorState = nextErrorState;
      this.stateChanges.next();
    }

    if (!this.required && nextRequired) {
      this.required = true;
      this.stateChanges.next();
    }
  }

  // MatFormFieldControl

  get empty(): boolean {
    return !this.value;
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).tagName.toLowerCase() !== 'input') {
      (event.target as HTMLElement).focus();
    }
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
  }

  // Update focus state
  onFocus(): void {
    this.focused = true;
    this.stateChanges.next();
  }

  onBlur(): void {
    this.focused = false;
    this.onTouched();
    this.stateChanges.next();
  }

  private hasRequiredValidator(validator?: ((control: any) => Record<string, any> | null) | null): boolean {
    if (!validator) {
      return false;
    }

    const validationResult = validator(new FormControl(''));
    return !!validationResult?.required;
  }

}
